import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../../../catalyst-components/button';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { useNavigate } from 'react-router-dom';
import { MainRoutes } from '../../../shared/models';

interface MultipleChoiceQuestion {
  question: string;
  options: string[];
}

interface MultipleChoiceSessionProps {
  sessionId: Id<"sessions">;
  questions: MultipleChoiceQuestion[];
  greeting?: string;
  signOff?: string;
  assistantDisplayName?: string;
  isComplete?: boolean;
  existingAnswers?: Array<{ question: string; selected_option: string }>;
  sessionMessages?: Array<{ role: string; content: string; }>;
}

export const MultipleChoiceSession: React.FC<MultipleChoiceSessionProps> = ({
  sessionId,
  questions,
  greeting,
  signOff,
  assistantDisplayName,
  isComplete = false,
  existingAnswers = [],
  sessionMessages = []
}) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Map<number, string>>(new Map());
  const [showResults, setShowResults] = useState(false);
  const hasInitialized = useRef(false);
  
  const addQuizMessage = useMutation(api.sessions.addQuizMessage);
  const updateMultipleChoiceQuestions = useMutation(api.sessions.updateMultipleChoiceQuestions);
  const completeQuizSession = useMutation(api.sessions.completeQuizSession);

  // Initialize from existing answers (completed session) or from in-progress messages
  useEffect(() => {
    // Skip if already initialized
    if (hasInitialized.current) {
      return;
    }

    // If session is complete, show results with all answers
    if (isComplete && existingAnswers.length > 0) {
      const answersMap = new Map<number, string>();
      existingAnswers.forEach((answer, index) => {
        answersMap.set(index, answer.selected_option);
      });
      setSelectedAnswers(answersMap);
      setShowResults(true);
      hasInitialized.current = true;
      return;
    }

    // If session is in progress, reconstruct state from messages
    if (!isComplete && sessionMessages && sessionMessages.length > 0) {
      // Count user messages to determine how many questions have been answered
      const userMessages = sessionMessages.filter(msg => msg.role === 'user');
      
      if (userMessages.length > 0) {
        const answersMap = new Map<number, string>();
        
        // Map user messages to their answers
        userMessages.forEach((msg, index) => {
          if (index < questions.length) {
            answersMap.set(index, msg.content);
          }
        });

        setSelectedAnswers(answersMap);
        
        // Set current question to the first unanswered one
        const nextQuestionIndex = userMessages.length;
        if (nextQuestionIndex < questions.length) {
          setCurrentQuestionIndex(nextQuestionIndex);
        } else {
          // All questions answered but not marked complete yet
          setShowResults(true);
        }
        
        hasInitialized.current = true;
      }
    }
  }, [existingAnswers, sessionMessages, isComplete, questions.length]);

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasAnsweredCurrent = selectedAnswers.has(currentQuestionIndex);
  const allQuestionsAnswered = selectedAnswers.size === questions.length;

  const handleOptionSelect = (option: string) => {
    if (isComplete) return;
    
    const newAnswers = new Map(selectedAnswers);
    newAnswers.set(currentQuestionIndex, option);
    setSelectedAnswers(newAnswers);
  };

  const handleNext = async () => {
    if (!hasAnsweredCurrent || isComplete) return;

    // Record the question and answer as messages
    const selectedOption = selectedAnswers.get(currentQuestionIndex)!;
    
    try {
      // Add question as assistant message
      await addQuizMessage({
        session_id: sessionId,
        role: 'assistant',
        content: `**Question ${currentQuestionIndex + 1}:** ${currentQuestion.question}`
      });

      // Add answer as user message
      await addQuizMessage({
        session_id: sessionId,
        role: 'user',
        content: selectedOption
      });

      if (isLastQuestion) {
        // Save all answers to the session
        const answersArray = Array.from(selectedAnswers.entries()).map(([index, option]) => ({
          question: questions[index].question,
          selected_option: option
        }));

        await updateMultipleChoiceQuestions({
          session_id: sessionId,
          multiple_choice_questions: answersArray
        });

        // Add sign-off message if provided
        if (signOff) {
          await addQuizMessage({
            session_id: sessionId,
            role: 'assistant',
            content: signOff
          });
        }

        // Complete the quiz session (this will trigger OpenAI conversation creation and analysis)
        await completeQuizSession({ session_id: sessionId });
        
        // Navigate to progress page
        navigate(MainRoutes.Progress);
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    } catch (error) {
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0 && !showResults) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const progress = ((selectedAnswers.size / questions.length) * 100).toFixed(0);

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
        <p className="text-primary-5 text-lg mb-4">No questions available for this quiz.</p>
        <Button color="primary" onClick={() => navigate(MainRoutes.Progress)}>
          Back to Progress
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      {assistantDisplayName && (
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-primary-5 mb-2">{assistantDisplayName}</h1>
          {greeting && !showResults && (
            <p className="text-primary-5 opacity-75">{greeting}</p>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-primary-5 font-medium">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-sm text-primary-5 font-medium">
            {progress}% Complete
          </span>
        </div>
        <div className="w-full bg-primary-2 rounded-full h-3">
          <div
            className="bg-primary-5 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-primary-1 border-2 border-primary-4 rounded-xl p-8 mb-6">
        <h2 className="text-2xl font-semibold text-primary-5 mb-6">
          {currentQuestion.question}
        </h2>
        
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const isSelected = selectedAnswers.get(currentQuestionIndex) === option;
            return (
              <button
                key={index}
                onClick={() => handleOptionSelect(option)}
                disabled={isComplete}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-primary-5 bg-primary-2 font-semibold'
                    : 'border-primary-4 bg-primary-1 hover:border-primary-5 hover:bg-primary-2'
                } ${isComplete ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <div className="flex items-center">
                  <div
                    className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      isSelected ? 'border-primary-5 bg-primary-5' : 'border-primary-4'
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 bg-primary-1 rounded-full" />
                    )}
                  </div>
                  <span className="text-primary-5">{option}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4 justify-between">
        <Button
          color="light"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0 || isComplete}
        >
          Previous
        </Button>
        <Button
          color="primary"
          onClick={handleNext}
          disabled={!hasAnsweredCurrent || isComplete}
        >
          {isLastQuestion ? 'Finish Quiz' : 'Next Question'}
        </Button>
      </div>
    </div>
  );
};
